/** Calendar days as `YYYY-MM-DD` strings; the math runs in UTC so the browser's zone never shifts a day (BB-108). */
const DAY = 86_400_000;

export type CalendarViewMode = 'week' | 'month';
export interface CalendarSearch {
  view?: CalendarViewMode;
  date?: string;
}

export const isDay = (value: unknown): value is string => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));
export const calendarSearch = (search: Record<string, unknown>): CalendarSearch => ({
  ...(search.view === 'month' ? { view: 'month' as const } : {}),
  ...(isDay(search.date) ? { date: search.date } : {}),
});

export const addDays = (day: string, n: number): string => new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY).toISOString().slice(0, 10);
/** 0 = Monday … 6 = Sunday. */
export const weekdayIndex = (day: string): number => (new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7;
export const weekStart = (day: string): string => addDays(day, -weekdayIndex(day));
export const addMonths = (day: string, n: number): string => {
  const d = new Date(`${day.slice(0, 7)}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + n);
  return d.toISOString().slice(0, 10);
};
export const todayLocal = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

/** Week: Monday…Sunday. Month: whole weeks from the Monday before the 1st to the Sunday after the last day. */
export function rangeOf(view: CalendarViewMode, day: string): { from: string; to: string } {
  if (view === 'week') {
    const from = weekStart(day);
    return { from, to: addDays(from, 6) };
  }
  const first = `${day.slice(0, 7)}-01`;
  const last = addDays(addMonths(first, 1), -1);
  return { from: weekStart(first), to: addDays(weekStart(last), 6) };
}

export function daysOf(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

const fmt = (day: string, options: Intl.DateTimeFormatOptions) => new Date(`${day}T00:00:00Z`).toLocaleDateString('en-GB', { timeZone: 'UTC', ...options });
export const dayLabel = (day: string): string => fmt(day, { weekday: 'short', day: 'numeric', month: 'short' });
export const monthLabel = (day: string): string => fmt(day, { month: 'long', year: 'numeric' });
export const rangeLabel = (from: string, to: string): string => `${fmt(from, { day: 'numeric', month: 'short' })} – ${fmt(to, { day: 'numeric', month: 'short', year: 'numeric' })}`;
export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
